import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useTheme } from "@/hooks/useTheme";
import { mangaList, categories } from "@/mocks/manga";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { isDark, toggleTheme } = useTheme();

  const manga = mangaList.find((m) => m.id === Number(id));

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
          <h2 className="text-xl text-foreground-950 mb-2">漫画未找到</h2>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === manga.category);

  const relatedManga = mangaList
    .filter((m) => m.category === manga.category && m.id !== manga.id)
    .slice(0, 4);

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `http://biensur.cn/article/${manga.id}/#article`,
        headline: `${manga.title} - 漫蛙免费漫画在线阅读`,
        description: manga.description,
        author: { "@type": "Person", name: manga.author },
        datePublished: "2024-01-15T08:00:00+08:00",
        dateModified: new Date().toISOString(),
        image: manga.imageUrl,
        inLanguage: "zh-Hans",
        isPartOf: { "@id": "http://biensur.cn/#website" },
        publisher: { "@id": "http://biensur.cn/#organization" },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `http://biensur.cn/article/${manga.id}/#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "http://biensur.cn/" },
          { "@type": "ListItem", position: 2, name: `${category?.name || ""}漫画`, item: `http://biensur.cn/category/${manga.category}/` },
          { "@type": "ListItem", position: 3, name: manga.title, item: `http://biensur.cn/article/${manga.id}/` },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar isDark={isDark} toggleTheme={toggleTheme} />

        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-foreground-950 transition-colors whitespace-nowrap">首页</Link>
              <span>/</span>
              <Link to={`/category/${manga.category}`} className="hover:text-foreground-950 transition-colors whitespace-nowrap">
                {category?.name}漫画
              </Link>
              <span>/</span>
              <span className="text-foreground-950 font-medium">{manga.title}</span>
            </nav>
          </div>

          {/* Article header */}
          <article className="max-w-5xl mx-auto px-4 md:px-6 pb-10">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              {/* Cover image */}
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={manga.imageUrl}
                    alt={manga.title}
                    title={`${manga.title} - 漫蛙免费漫画在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="inline-block bg-accent-100 text-accent-700 text-xs px-2.5 py-1 rounded-full">
                    {category?.name}
                  </span>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${
                    manga.status === "连载中"
                      ? "bg-primary-100 text-primary-700"
                      : "bg-secondary-100 text-secondary-700"
                  }`}>
                    {manga.status}
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4 leading-tight">
                  {manga.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-6 text-sm">
                  <span className="flex items-center gap-1 text-foreground-600">
                    <i className="ri-user-line text-accent-500"></i>
                    {manga.author}
                  </span>
                  <span className="flex items-center gap-1 text-foreground-600">
                    <i className="ri-star-fill text-primary-500"></i>
                    {manga.rating}
                  </span>
                  <span className="flex items-center gap-1 text-foreground-600">
                    <i className="ri-eye-line text-accent-500"></i>
                    {manga.views}
                  </span>
                  <span className="flex items-center gap-1 text-foreground-600">
                    <i className="ri-book-open-line text-secondary-500"></i>
                    共{manga.chapters}话
                  </span>
                </div>

                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  {manga.description}
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-play-circle-line"></i>
                    开始阅读第1话
                  </button>
                  <button className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 border border-background-200/70 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-heart-line"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Description content */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">剧情介绍</h2>
              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: manga.descriptionHtml }}
              />
            </div>
          </section>

          {/* Chapter list placeholder */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">章节列表</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {Array.from({ length: Math.min(20, manga.chapters) }, (_, i) => (
                  <button
                    key={i}
                    className="flex items-center justify-between px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-700 hover:border-accent-300 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span>第{i + 1}话</span>
                    {i < 3 && (
                      <span className="text-xs text-accent-500">NEW</span>
                    )}
                  </button>
                ))}
              </div>
              {manga.chapters > 20 && (
                <p className="text-center text-sm text-foreground-400 mt-6">
                  ... 共{manga.chapters}话，使用App查看全部章节
                </p>
              )}
            </div>
          </section>

          {/* Related manga */}
          {relatedManga.length > 0 && (
            <section className="py-10 md:py-14 bg-background-100">
              <div className="max-w-5xl mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                  {relatedManga.map((m) => (
                    <Link
                      key={m.id}
                      to={`/article/${m.id}`}
                      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[3/4] overflow-hidden">
                        <img
                          src={m.imageUrl}
                          alt={m.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-foreground-950 mb-1">{m.title}</h4>
                        <span className="text-xs text-foreground-500">{m.author}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}